// src/pages/settings/sms/components/TriggerFormModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { X, Save, RefreshCw, Info, Search, Loader2 } from 'lucide-react';
import { getShops } from '../../../../services/api/shops';
import { getProducts } from '../../../../services/api/productSearch';
import styles from './TriggerFormModal.module.css';

const VARIABLES = [
  { key: '{{orderNumber}}', label: 'Order Number' },
  { key: '{{customerName}}', label: 'Customer Name' },
  { key: '{{codAmount}}', label: 'COD Amount' },
  { key: '{{grandTotal}}', label: 'Grand Total' },
  { key: '{{cityName}}', label: 'City Name' },
  { key: '{{itemDescription}}', label: 'Item Description' },
  { key: '{{courierName}}', label: 'Courier Name' },
];

const TriggerFormModal = ({ trigger, triggerOptions, smsLength, onSubmit, onClose }) => {
  const [eventType, setEventType] = useState(trigger?.eventType || 'orderStatus');
  const [triggerId, setTriggerId] = useState(trigger ? String(trigger.triggerId) : '');
  const [message, setMessage] = useState(trigger?.message || '');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const textareaRef = useRef(null);

  // ── Shop limit state ───────────────────────────────────────────────────────
  const [limitShops, setLimitShops] = useState(
    trigger?.shops !== null && trigger?.shops !== undefined && Array.isArray(trigger.shops)
  );
  const [selectedShopIds, setSelectedShopIds] = useState(
    trigger?.shops ?? []
  );
  const [shops, setShops] = useState([]);
  const [isLoadingShops, setIsLoadingShops] = useState(false);

  // ── Product limit state ───────────────────────────────────────────────────
  const [limitProducts, setLimitProducts] = useState(
    trigger?.products !== null && trigger?.products !== undefined && Array.isArray(trigger.products)
  );
  const [selectedProductIds, setSelectedProductIds] = useState(
    trigger?.products ?? []
  );
  // Map of productId → display name for selected products
  const [selectedProductObjects, setSelectedProductObjects] = useState({});
  const [productSearchResults, setProductSearchResults] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const productSearchTimer = useRef(null);

  // Load shops once when limitShops is first enabled
  useEffect(() => {
    if (limitShops && shops.length === 0) {
      setIsLoadingShops(true);
      getShops()
        .then(res => setShops(res.shops || res || []))
        .catch(() => {})
        .finally(() => setIsLoadingShops(false));
    }
  }, [limitShops]);

  // When editing an existing trigger that has products, pre-load their names
  useEffect(() => {
    if (trigger?.products?.length && Object.keys(selectedProductObjects).length === 0) {
      // We can't reverse-lookup names without an API, so we just store IDs.
      // The display will fall back to showing "Product #ID" for pre-existing ones.
      const initial = {};
      trigger.products.forEach(id => { initial[id] = { productId: id, name: `Product #${id}` }; });
      setSelectedProductObjects(initial);
    }
  }, []);

  // Product search with debounce
  useEffect(() => {
    if (!limitProducts) return;
    if (!productSearchTerm || productSearchTerm.length < 2) {
      setProductSearchResults([]);
      return;
    }
    clearTimeout(productSearchTimer.current);
    productSearchTimer.current = setTimeout(async () => {
      try {
        setIsLoadingProducts(true);
        const response = await getProducts({ search: productSearchTerm, limit: 50, page: 1 });
        const list = (response.products || []).map(p => ({
          productId: p.productId,
          name: p.displayName || p.name,
          sku: p.sku || '',
        }));
        setProductSearchResults(list);
      } catch {
        setProductSearchResults([]);
      } finally {
        setIsLoadingProducts(false);
      }
    }, 350);
    return () => clearTimeout(productSearchTimer.current);
  }, [productSearchTerm, limitProducts]);

  const statusOptions = eventType === 'orderStatus'
    ? triggerOptions.orderStatuses
    : triggerOptions.courierStatuses;

  const insertVariable = (variable) => {
    const ta = textareaRef.current;
    if (!ta) { setMessage(prev => prev + variable); return; }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newMsg = message.substring(0, start) + variable + message.substring(end);
    setMessage(newMsg);
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + variable.length, start + variable.length);
    }, 0);
  };

  const handleToggleShop = (shopId) => {
    setSelectedShopIds(prev =>
      prev.includes(shopId) ? prev.filter(id => id !== shopId) : [...prev, shopId]
    );
  };

  const handleSelectProduct = (product) => {
    if (selectedProductIds.includes(product.productId)) return;
    setSelectedProductIds(prev => [...prev, product.productId]);
    setSelectedProductObjects(prev => ({ ...prev, [product.productId]: product }));
    setProductSearchTerm('');
    setProductSearchResults([]);
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProductIds(prev => prev.filter(id => id !== productId));
    setSelectedProductObjects(prev => {
      const next = { ...prev };
      delete next[productId];
      return next;
    });
  };

  const validate = () => {
    const errs = {};
    if (!triggerId) errs.triggerId = 'Please select a status';
    if (!message.trim()) errs.message = 'Message is required';
    if (message.length > smsLength) errs.message = `Message exceeds ${smsLength} character limit`;
    if (limitShops && selectedShopIds.length === 0) errs.shops = 'Select at least one shop or uncheck the limit';
    if (limitProducts && selectedProductIds.length === 0) errs.products = 'Select at least one product or uncheck the limit';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setIsSaving(true);
      await onSubmit({
        eventType,
        triggerId: parseInt(triggerId),
        message,
        shops: limitShops ? selectedShopIds : null,
        products: limitProducts ? selectedProductIds : null,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const charCount = message.length;
  const isOverLimit = charCount > smsLength;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>{trigger ? 'Edit Trigger' : 'Add Trigger'}</h3>
          <button className={styles.closeBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div className={styles.body}>
          {/* Event type */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Event Type</label>
            <div className={styles.segmented}>
              {['orderStatus', 'courierStatus'].map(type => (
                <button
                  key={type}
                  className={`${styles.segBtn} ${eventType === type ? styles.segActive : ''}`}
                  onClick={() => { setEventType(type); setTriggerId(''); }}
                >
                  {type === 'orderStatus' ? 'Order Status' : 'Courier Status'}
                </button>
              ))}
            </div>
          </div>

          {/* Status picker */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              {eventType === 'orderStatus' ? 'When order status changes to' : 'When courier status changes to'}
            </label>
            <select
              className={`${styles.select} ${errors.triggerId ? styles.inputError : ''}`}
              value={triggerId}
              onChange={e => setTriggerId(e.target.value)}
            >
              <option value="">Select status...</option>
              {statusOptions.map(s => (
                <option key={s.id} value={s.id}>
                  {eventType === 'orderStatus'
                    ? s.label
                    : `${s.statusLabel || s.statusCode}${s.courierCompany ? ` (${s.courierCompany.name})` : ''}`}
                </option>
              ))}
            </select>
            {errors.triggerId && <span className={styles.error}>{errors.triggerId}</span>}
          </div>

          {/* ── Shop limit ─────────────────────────────────────────────────── */}
          <div className={styles.formGroup}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={limitShops}
                onChange={e => {
                  setLimitShops(e.target.checked);
                  if (!e.target.checked) setSelectedShopIds([]);
                }}
                className={styles.checkbox}
              />
              <span className={styles.checkboxLabel}>Limit to specific shops</span>
            </label>
            <small className={styles.hint}>If checked, SMS is only sent for orders belonging to the selected shops</small>

            {limitShops && (
              <div className={styles.filterBox}>
                {isLoadingShops ? (
                  <div className={styles.loadingRow}>
                    <Loader2 size={14} className={styles.spin} />
                    <span>Loading shops...</span>
                  </div>
                ) : (
                  <>
                    <div className={styles.checkList}>
                      {shops.map(shop => (
                        <label key={shop.id} className={styles.checkItem}>
                          <input
                            type="checkbox"
                            checked={selectedShopIds.includes(shop.id)}
                            onChange={() => handleToggleShop(shop.id)}
                            className={styles.checkbox}
                          />
                          <span>{shop.name}</span>
                        </label>
                      ))}
                      {shops.length === 0 && (
                        <span className={styles.emptyHint}>No shops found</span>
                      )}
                    </div>
                    {selectedShopIds.length > 0 && (
                      <div className={styles.selectedCount}>
                        {selectedShopIds.length} shop{selectedShopIds.length !== 1 ? 's' : ''} selected
                      </div>
                    )}
                  </>
                )}
                {errors.shops && <span className={styles.error}>{errors.shops}</span>}
              </div>
            )}
          </div>

          {/* ── Product limit ──────────────────────────────────────────────── */}
          <div className={styles.formGroup}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={limitProducts}
                onChange={e => {
                  setLimitProducts(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedProductIds([]);
                    setSelectedProductObjects({});
                    setProductSearchTerm('');
                    setProductSearchResults([]);
                  }
                }}
                className={styles.checkbox}
              />
              <span className={styles.checkboxLabel}>Limit to specific products</span>
            </label>
            <small className={styles.hint}>If checked, SMS is only sent when the order contains at least one of the selected products</small>

            {limitProducts && (
              <div className={styles.filterBox}>
                {/* Selected product tags */}
                {selectedProductIds.length > 0 && (
                  <div className={styles.tagList}>
                    {selectedProductIds.map(pid => {
                      const obj = selectedProductObjects[pid];
                      return (
                        <span key={pid} className={styles.tag}>
                          {obj?.name || `Product #${pid}`}
                          <button
                            type="button"
                            className={styles.tagRemove}
                            onClick={() => handleRemoveProduct(pid)}
                          >
                            <X size={11} />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Search input */}
                <div className={styles.searchWrapper}>
                  <Search size={14} className={styles.searchIcon} />
                  <input
                    type="text"
                    className={styles.searchInput}
                    value={productSearchTerm}
                    onChange={e => setProductSearchTerm(e.target.value)}
                    placeholder="Search products..."
                  />
                  {isLoadingProducts && <Loader2 size={14} className={`${styles.spin} ${styles.searchSpinner}`} />}
                </div>

                {/* Search results dropdown */}
                {productSearchResults.length > 0 && (
                  <div className={styles.searchDropdown}>
                    {productSearchResults
                      .filter(p => !selectedProductIds.includes(p.productId))
                      .map(p => (
                        <button
                          key={p.productId}
                          type="button"
                          className={styles.searchResult}
                          onClick={() => handleSelectProduct(p)}
                        >
                          <span className={styles.resultName}>{p.name}</span>
                          {p.sku && <span className={styles.resultSku}>{p.sku}</span>}
                        </button>
                      ))}
                    {productSearchResults.filter(p => !selectedProductIds.includes(p.productId)).length === 0 && (
                      <span className={styles.emptyHint}>All results already selected</span>
                    )}
                  </div>
                )}

                {selectedProductIds.length === 0 && !productSearchTerm && (
                  <span className={styles.emptyHint}>Search and select products above</span>
                )}

                {errors.products && <span className={styles.error}>{errors.products}</span>}
              </div>
            )}
          </div>

          {/* Variable helper */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <Info size={13} /> Insert Variable
            </label>
            <div className={styles.variableChips}>
              {VARIABLES.map(v => (
                <button key={v.key} className={styles.varChip} onClick={() => insertVariable(v.key)} type="button">
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          {/* Message */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Message</label>
            <textarea
              ref={textareaRef}
              className={`${styles.textarea} ${errors.message ? styles.inputError : ''}`}
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={5}
              placeholder="Type your message here. Use the variable buttons above to insert dynamic values."
            />
            <div className={styles.charBar}>
              <span className={isOverLimit ? styles.charOver : styles.charOk}>
                {charCount} / {smsLength} characters
              </span>
              {isOverLimit && <span className={styles.error}>Exceeds limit</span>}
            </div>
            {errors.message && <span className={styles.error}>{errors.message}</span>}
          </div>
        </div>

        <div className={styles.footer}>
          <button className={styles.secondaryBtn} onClick={onClose} disabled={isSaving}>Cancel</button>
          <button className={styles.primaryBtn} onClick={handleSubmit} disabled={isSaving}>
            {isSaving
              ? <><RefreshCw size={15} className={styles.spin} /> Saving...</>
              : <><Save size={15} /> {trigger ? 'Update' : 'Create'} Trigger</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TriggerFormModal;